import React, { useState } from 'react';
import { Plus, Edit2, Trash2, GripVertical, Check, X, ChevronDown, ChevronUp } from 'lucide-react';

const TradingPlanManager = ({ 
  rules, 
  onAddRule, 
  onUpdateRule, 
  onDeleteRule, 
  onReorderRules,
  isLoading 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [newRuleText, setNewRuleText] = useState('');
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  
  const handleAddRule = async () => {
    if (!newRuleText.trim()) return;
    await onAddRule(newRuleText.trim());
    setNewRuleText('');
  };
  
  const handleStartEdit = (rule) => {
    setEditingRuleId(rule.id);
    setEditingText(rule.rule_text);
  };
  
  const handleSaveEdit = async () => {
    if (!editingText.trim()) return;
    await onUpdateRule(editingRuleId, { rule_text: editingText.trim() });
    setEditingRuleId(null);
    setEditingText('');
  };
  
  const handleCancelEdit = () => {
    setEditingRuleId(null);
    setEditingText('');
  };
  
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newRules = [...rules];
    const draggedRule = newRules[draggedIndex];
    newRules.splice(draggedIndex, 1);
    newRules.splice(index, 0, draggedRule);
    
    // Update orders
    const ruleOrders = newRules.map((rule, i) => ({ id: rule.id, order: i }));
    onReorderRules(ruleOrders);
    setDraggedIndex(index);
  };
  
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };
  
  return (
    <div className="trading-plan-manager">
      <div 
        className="plan-manager-header clickable"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="plan-manager-title">
          <span className="plan-icon">📝</span>
          <span>My Trading Plan</span>
          <span className="rules-count">{rules.length} rules</span>
        </div>
        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </div>
      
      {isExpanded && (
        <div className="plan-manager-content">
          <p className="plan-description">
            Create your trading rules below. Track your adherence daily to build discipline.
          </p>
          
          {/* Add new rule */}
          <div className="add-rule-form">
            <input
              type="text"
              placeholder="Add a new rule (e.g., 'No trades before 9:45am EST')"
              value={newRuleText}
              onChange={(e) => setNewRuleText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddRule()}
              className="rule-input"
            />
            <button 
              className="add-rule-btn"
              onClick={handleAddRule}
              disabled={!newRuleText.trim() || isLoading}
            >
              <Plus size={16} />
              Add Rule
            </button>
          </div>
          
          {/* Rules list */}
          {rules.length > 0 ? (
            <div className="rules-list">
              {rules.map((rule, index) => (
                <div
                  key={rule.id}
                  className={`rule-item ${draggedIndex === index ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="rule-drag-handle">
                    <GripVertical size={16} />
                  </div>
                  
                  <div className="rule-number">{index + 1}</div>
                  
                  {editingRuleId === rule.id ? (
                    <div className="rule-edit-form">
                      <input
                        type="text"
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        className="rule-edit-input"
                        autoFocus
                      />
                      <button className="rule-save-btn" onClick={handleSaveEdit}>
                        <Check size={14} />
                      </button>
                      <button className="rule-cancel-btn" onClick={handleCancelEdit}>
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="rule-text">{rule.rule_text}</span>
                      <div className="rule-actions">
                        <button 
                          className="rule-action-btn edit"
                          onClick={() => handleStartEdit(rule)}
                          title="Edit rule"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button 
                          className="rule-action-btn delete"
                          onClick={() => onDeleteRule(rule.id)}
                          title="Delete rule"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-rules-message">
              No trading rules yet. Add your first rule above to start tracking your discipline.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TradingPlanManager;
